import type { JSX } from 'react';
import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { formatDate, defaultDateRange } from '@/lib/utils';
import { userFilterOptions } from '@/lib/user-filter-options';
import { Select } from '@/components/shared/select';
import type { ClientErrorLogEntry } from '@echo-work-platform/shared-types';

const SOURCE_OPTIONS = [
  { value: '', label: 'Összes forrás' },
  { value: 'renderer-uncaught', label: 'Renderer – uncaught' },
  { value: 'renderer-rejection', label: 'Renderer – rejection' },
  { value: 'model-generation', label: 'Modell / generálás' },
  { value: 'main-process', label: 'Main process' },
  { value: 'console-error', label: 'Console error' },
  { value: 'console-warn', label: 'Console warn' },
];

// datetime-local (no timezone) → ISO-8601 the backend's z.string().datetime() accepts.
function toIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function ErrorLogs(): JSX.Element {
  const defaults = useMemo(() => defaultDateRange(), []);
  const [userId, setUserId] = useState('');
  const [source, setSource] = useState('');
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [expanded, setExpanded] = useState<string | null>(null);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (userId) p.set('userId', userId);
    if (source) p.set('source', source);
    const fromIso = toIso(from);
    const toIsoVal = toIso(to);
    if (fromIso) p.set('from', fromIso);
    if (toIsoVal) p.set('to', toIsoVal);
    p.set('limit', '500');
    return p.toString();
  }, [userId, source, from, to]);

  const logs = useQuery({
    queryKey: ['/api/admin/error-logs', qs],
    queryFn: () => api.get<ClientErrorLogEntry[]>(`/api/admin/error-logs?${qs}`),
  });

  const userOptions = useMemo(() => userFilterOptions(logs.data, userId), [logs.data, userId]);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Hibanapló</h2>

      <div className="card flex flex-wrap items-end gap-4 p-4">
        <label className="flex flex-col gap-1 text-xs font-medium text-neutral-600">
          Felhasználó
          <Select value={userId} onChange={setUserId} options={userOptions} className="w-56" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-neutral-600">
          Forrás
          <Select value={source} onChange={setSource} options={SOURCE_OPTIONS} className="w-48" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-neutral-600">
          Ettől
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="input w-52"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-neutral-600">
          Eddig
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="input w-52"
          />
        </label>
        <button
          className="btn-secondary"
          onClick={() => {
            setUserId('');
            setSource('');
            setFrom(defaults.from);
            setTo(defaults.to);
          }}
        >
          Szűrők törlése
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Időpont</th>
              <th className="px-4 py-2 text-left">Felhasználó</th>
              <th className="px-4 py-2 text-left">Forrás</th>
              <th className="px-4 py-2 text-left">Szint</th>
              <th className="px-4 py-2 text-left">Üzenet</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {logs.data?.map((e) => {
              const isOpen = expanded === e.id;
              return (
                <Fragment key={e.id}>
                  <tr
                    className="cursor-pointer hover:bg-neutral-50"
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                  >
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(e.occurredAt)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-neutral-600">
                      {e.userEmail ?? e.userId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2">
                      <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs">
                        {e.source}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{e.level}</td>
                    <td className="max-w-xl truncate px-4 py-2">{e.message}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-neutral-50">
                      <td colSpan={5} className="px-4 py-3">
                        <div className="space-y-2 text-xs">
                          <div className="whitespace-pre-wrap break-words font-medium">{e.message}</div>
                          {e.sessionId && <div className="text-neutral-500">session: {e.sessionId}</div>}
                          {e.appVersion && <div className="text-neutral-500">app: {e.appVersion}</div>}
                          {e.stack && (
                            <pre className="overflow-auto rounded bg-code p-3 font-mono text-[11px] leading-relaxed text-code-foreground">
                              {e.stack}
                            </pre>
                          )}
                          {e.context && (
                            <pre className="overflow-auto rounded border border-neutral-200 bg-card p-3 font-mono text-[11px]">
                              {JSON.stringify(e.context, null, 2)}
                            </pre>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {logs.data?.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  Nincs a szűrőnek megfelelő hibabejegyzés.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
